/**
 * The history under a box (docs/ux/workspace.md): changes QA applied without
 * approval (with the doctor's Revert), open and answered questions, the
 * declined list kept visible, the house-style list with per-change Undo, and
 * the working notes that never enter the report.
 */

import { cn } from 'cn';
import { ChevronRightIcon } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Clarification, HeardWord, StyleNotice, StyleRecord, Suggestion, WorkspaceNotes, WorkspaceRole } from '@/app/types';
import { StateChip } from '@/components/matter/StateChip';
import { changeSummary, WordDiff } from '@/components/matter/WordDiff';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { pluralise } from '@/lib/format';
import { heardSentence } from './describe';
import { OFFLINE_TIP } from './EventCluster';

export const REASON_MAX = 2000;

/** A row's reason input with the counter that appears near the limit. */
export function ReasonInput({ id, label, placeholder, value, onChange, onEnter, phone }: { id: string; label: string; placeholder: string; value: string; onChange: (v: string) => void; onEnter?: () => void; phone?: boolean }) {
  const over = value.length > REASON_MAX;
  const near = value.length >= REASON_MAX - 200;
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <Input
        id={id}
        aria-label={label}
        aria-invalid={over || undefined}
        aria-describedby={near ? `${id}-count` : undefined}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (onEnter && e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onEnter(); } }}
        className={cn('bg-beam-paper', phone ? 'h-11 text-base' : 'h-8 text-[13px]')}
      />
      {near ? <span id={`${id}-count`} className={cn('text-xs', over ? 'text-beam-error' : 'text-beam-text-muted')}>{over ? `At most ${REASON_MAX} characters` : `${value.length} of ${REASON_MAX}`}</span> : null}
    </div>
  );
}

function Who({ s }: { s: Pick<Suggestion, 'author' | 'round'> }) {
  return <span className="text-[13px] text-beam-text-muted">{s.author}, round {s.round}</span>;
}

export function AppliedList({ applied, role, offline, busyId, onRevert, phone }: { applied: Suggestion[]; role: WorkspaceRole; offline: boolean; busyId: string | null; onRevert: (id: string) => void; phone?: boolean }) {
  const [confirming, setConfirming] = useState<string | null>(null);
  if (applied.length === 0) return null;
  return (
    <section aria-label="Applied by QA without approval" data-slot="applied-list" className="flex flex-col gap-2 rounded-xl border border-beam-line border-l-[3px] border-l-beam-warning bg-beam-paper p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="eyebrow text-beam-text-soft">Applied by QA without approval</h3>
        <span className="text-[13px] text-beam-text-muted">{pluralise(applied.length, 'change')}</span>
      </div>
      <ul className="flex flex-col divide-y divide-beam-line">
        {applied.map((s) => (
          <li key={s.id} className="flex flex-col gap-2 py-2 md:flex-row md:items-center md:gap-3">
            <div className="min-w-0 flex-1 text-[15px]"><WordDiff ops={s.ops} /></div>
            <div className="flex flex-wrap items-center gap-2">
              <Who s={s} />{s.reason ? <span className="text-[13px] text-beam-text-muted">{s.reason}</span> : null}
              {role === 'doctor' ? (
                confirming === s.id ? (
                  <span className="flex items-center gap-2" role="group" aria-label="Put the original wording back?">
                    <span className="text-[13px]">Put the original wording back?</span>
                    <Button autoFocus variant="outline" size="sm" className={phone ? 'h-11' : 'h-8'} disabled={offline || busyId !== null} onClick={() => { onRevert(s.id); setConfirming(null); }} onKeyDown={(e) => { if (e.key === 'Escape') setConfirming(null); }}>{busyId === s.id ? 'Reverting' : 'Revert'}</Button>
                    <Button variant="ghost" size="sm" className={phone ? 'h-11' : 'h-8'} onClick={() => setConfirming(null)}>Keep</Button>
                  </span>
                ) : (
                  <OfflineWrap offline={offline}>
                    <Button variant="outline" size="sm" className={phone ? 'h-11' : 'h-8'} disabled={offline || busyId !== null} aria-label={`Revert change: ${changeSummary(s.before, s.after)}`} onClick={() => setConfirming(s.id)}>Revert</Button>
                  </OfflineWrap>
                )
              ) : <StateChip label="Applied" tone="neutral" size="sm" />}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function OfflineWrap({ offline, children }: { offline: boolean; children: ReactNode }) {
  if (!offline) return <>{children}</>;
  return <Tooltip><TooltipTrigger asChild><span tabIndex={0}>{children}</span></TooltipTrigger><TooltipContent>{OFFLINE_TIP}</TooltipContent></Tooltip>;
}

export function ClarificationList({ open, role, offline, busyId, onAddress, phone }: { open: Clarification[]; role: WorkspaceRole; offline: boolean; busyId: string | null; onAddress: (id: string, answer: string) => void; phone?: boolean }) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  if (open.length === 0) return null;
  return (
    <ul data-slot="clarification-list" className="flex flex-col gap-2">
      {open.map((c) => {
        const answer = answers[c.id] ?? '';
        return (
          <li key={c.id} className="flex flex-col gap-2 rounded-xl border border-beam-warning bg-beam-warning-bg p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="eyebrow text-beam-warning">{c.aboutSuggestionId ? 'Question from QA about a declined edit' : 'Question from QA'}</span>
              <span className="text-[13px] text-beam-text-muted">{c.askedBy}, round {c.round}</span>
            </div>
            <p className="text-base">{c.question}</p>
            {role === 'doctor' ? (
              <div className={cn('flex gap-2', phone ? 'flex-col' : 'items-start')}>
                <ReasonInput id={`reply-${c.id}`} label="Your reply to QA (optional)" placeholder="Your reply to QA (optional)" value={answer} onChange={(v) => setAnswers((a) => ({ ...a, [c.id]: v }))} onEnter={() => onAddress(c.id, answer)} phone={phone} />
                <OfflineWrap offline={offline}>
                  <Button className={phone ? 'h-11' : 'h-8'} disabled={offline || busyId !== null || answer.length > REASON_MAX} onClick={() => onAddress(c.id, answer)}>{busyId === c.id ? 'Closing' : 'Reply and close'}</Button>
                </OfflineWrap>
              </div>
            ) : <StateChip label="Waiting on the doctor" tone="neutral" size="sm" className="self-start" />}
          </li>
        );
      })}
    </ul>
  );
}

export function AnsweredList({ answered }: { answered: Clarification[] }) {
  if (answered.length === 0) return null;
  return (
    <CollapsibleList title={pluralise(answered.length, 'answered question')} slot="answered-list">
      <ul className="flex flex-col divide-y divide-beam-line">
        {answered.map((c) => (
          <li key={c.id} className="flex flex-col gap-1 py-2">
            <p className="text-[15px]">{c.question}</p>
            <p className="text-[13px] text-beam-text-soft">{c.answer ? `Doctor: ${c.answer}` : 'Closed by the doctor without a reply'}</p>
            <span className="text-[13px] text-beam-text-muted">{c.askedBy}, round {c.round}</span>
          </li>
        ))}
      </ul>
    </CollapsibleList>
  );
}

export function DeclinedList({ rejected, role, offline, onAsk, phone, defaultOpen }: { rejected: Suggestion[]; role: WorkspaceRole; offline: boolean; onAsk: (aboutSuggestionId: string, question: string) => void; phone?: boolean; defaultOpen?: boolean }) {
  const [questions, setQuestions] = useState<Record<string, string>>({});
  const [blank, setBlank] = useState<string | null>(null);
  if (rejected.length === 0) return null;
  const reverted = rejected.some((s) => s.state === 'reverted');
  const title = `${rejected.length} ${reverted ? 'declined or reverted' : 'declined'} by the doctor`;
  return (
    <CollapsibleList title={title} slot="declined-list" defaultOpen={defaultOpen}>
      <ul className="flex flex-col divide-y divide-beam-line">
        {rejected.map((s) => (
          <li key={s.id} className="flex flex-col gap-2 py-2">
            <div className="text-[15px]"><WordDiff ops={s.ops} /></div>
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-[13px] text-beam-text-muted">
              {s.reason ? <span>QA: {s.reason}</span> : null}
              <span>{s.state === 'reverted' ? 'Reverted by the doctor' : s.rejectionReason ? `Declined: ${s.rejectionReason}` : 'Declined, no reason given'}</span>
              <Who s={s} />
            </div>
            {role === 'qa' ? (
              <div className={cn('flex gap-2', phone ? 'flex-col' : 'items-start')}>
                <ReasonInput id={`ask-${s.id}`} label="Ask the doctor about this" placeholder="Ask the doctor about this" value={questions[s.id] ?? ''} onChange={(v) => { setQuestions((q) => ({ ...q, [s.id]: v })); setBlank(null); }} onEnter={() => ask(s.id)} phone={phone} />
                <div className="flex flex-col gap-1">
                  <Button variant="outline" className={phone ? 'h-11' : 'h-8'} disabled={offline} onClick={() => ask(s.id)}>Ask for clarification</Button>
                  {blank === s.id ? <span role="status" className="text-xs text-beam-error">Write the question first</span> : null}
                </div>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </CollapsibleList>
  );

  function ask(id: string) {
    const q = (questions[id] ?? '').trim();
    if (!q) { setBlank(id); return; }
    onAsk(id, q);
    setQuestions((x) => ({ ...x, [id]: '' }));
  }
}

export function StyleList({ records, notices, heard = [], offline, busy, onUndo, onConfirmHeard, phone }: { records: StyleRecord[]; notices: StyleNotice[]; heard?: HeardWord[]; offline: boolean; busy: boolean; onUndo: (id: string) => void; onConfirmHeard?: (id: string) => void; phone?: boolean }) {
  const likely = records.filter((r) => r.confidence === 'likely').length;
  return (
    <section aria-label="Changes to your words" data-slot="style-list" className="flex flex-col gap-2 rounded-xl bg-beam-cream-deep p-4">
      <h3 className="eyebrow text-beam-text-soft">Changes to your words</h3>
      <ul className="flex flex-col divide-y divide-beam-line">
        {heard.map((h) => {
          // First, because they ask for a decision (ADR-0002): the word as it reads, how sure it was and where in the audio; "Heard right" is the doctor's alone.
          const confirm = onConfirmHeard ? <Button variant="ghost" size="sm" className={phone ? 'h-11' : 'h-8'} disabled={offline || busy} aria-label={`Heard right: ${h.word}`} onClick={() => onConfirmHeard(h.id)}>Heard right</Button> : null;
          return (
            <li key={h.id} data-slot="heard-record" className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-[15px]">
              <span className="underline decoration-wavy decoration-beam-text-faint underline-offset-4">{h.word}</span>
              <span className="text-[13px] text-beam-text-muted">{heardSentence(h)}</span>
              {confirm ? <span className="ml-auto">{offline ? <OfflineWrap offline>{confirm}</OfflineWrap> : confirm}</span> : null}
            </li>
          );
        })}
        {records.map((r) => {
          const format = r.kind === 'format';
          const undo = (
            <Button variant="ghost" size="sm" className={phone ? 'h-11' : 'h-8'} disabled={offline || busy || !r.anchored} aria-label={format ? `Undo: ${r.note}` : `Undo: ${changeSummary(r.before, r.after)}`} onClick={() => onUndo(r.id)}>Undo</Button>
          );
          return (
            <li key={r.id} data-slot={format ? 'format-record' : r.kind === 'proof' ? 'proof-record' : 'style-record'} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-[15px]">
              {format ? (
                // The formatter's record covers a whole passage: its before and after are the passage, so the row carries the counts and the undo, not two copies of the text.
                <><span className="font-medium">{r.note}</span><span className="text-[13px] text-beam-text-muted">{r.before.length} characters, punctuation and layout only</span></>
              ) : (
                <><span className="text-beam-error line-through">{r.before}</span><span className="text-[13px] text-beam-text-muted">becomes</span><span className="text-beam-success underline decoration-2 underline-offset-2">{r.after}</span></>
              )}
              {r.confidence === 'likely' ? <span className="text-[13px] font-medium text-beam-warning">worth checking</span> : null}
              {format ? null : <span className="text-[13px] text-beam-text-muted">{r.note || r.rule}</span>}
              <span className="ml-auto">
                {!r.anchored ? <Tooltip><TooltipTrigger asChild><span tabIndex={0}>{undo}</span></TooltipTrigger><TooltipContent>The text around this change has moved, so it cannot be put back safely.</TooltipContent></Tooltip> : offline ? <OfflineWrap offline>{undo}</OfflineWrap> : undo}
              </span>
            </li>
          );
        })}
        {notices.map((n) => (
          <li key={`${n.rule}:${n.text}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-[15px]">
            <span className="underline decoration-dotted underline-offset-4">{n.text}</span><span className="text-[13px] text-beam-text-muted">left as written</span><span className="text-[13px] text-beam-text-muted">{n.reason}</span>
          </li>
        ))}
      </ul>
      {heard.length > 0 ? <p className="text-xs text-beam-text-muted">{pluralise(heard.length, 'word')} the recogniser was not sure of. Nothing was changed; a wavy line marks the word until it is edited or confirmed.</p> : null}
      {likely > 0 ? <p className="text-xs text-beam-text-muted">{pluralise(likely, 'change')} worth checking: the engine was not certain. Undo puts the original back; both are recorded.</p> : null}
    </section>
  );
}

export function CollapsibleList({ title, slot, defaultOpen, children }: { title: string; slot: string; defaultOpen?: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <Collapsible open={open} onOpenChange={setOpen} data-slot={slot}>
      <CollapsibleTrigger asChild>
        <button type="button" className="flex min-h-10 w-full items-center gap-2 rounded-md px-1 text-left text-[13px] font-medium text-beam-text-soft hover:bg-beam-cream-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-beam-signature md:min-h-8">
          <ChevronRightIcon aria-hidden="true" className={cn('size-4 transition-transform', open && 'rotate-90')} />{title}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-1">{children}</CollapsibleContent>
    </Collapsible>
  );
}

export function WorkingNotes({ notes, sectionKey, role, offline, mode, dictating, onSave, onDictate, onStop, interim, children, phone }: {
  notes: WorkspaceNotes;
  sectionKey: string;
  role: WorkspaceRole;
  offline: boolean;
  mode: 'edit' | 'read';
  dictating: 'idle' | 'listening' | 'finishing' | 'connecting';
  onSave: (text: string) => Promise<boolean>;
  onDictate: (caret: { start: number; end: number } | null) => void;
  onStop: () => void;
  interim: ReactNode;
  children?: ReactNode;
  phone?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(notes.text);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'failed' | 'stale'>('idle');
  const caret = useRef<{ start: number; end: number } | null>(null);
  const editable = mode === 'edit' || role === 'qa';
  const lastServer = useRef(notes.text);
  /** Set before a save so the styled text the server returns replaces the typed text, which differs from it by design. */
  const expectingServer = useRef(false);
  // Notes are shared by the doctor and QA: the server's text replaces the box unless this person has unsaved typing, which then needs Save again.
  useEffect(() => {
    if (notes.text === lastServer.current) return;
    const dirty = value !== lastServer.current;
    lastServer.current = notes.text;
    if (!dirty || dictating !== 'idle' || expectingServer.current) { setValue(notes.text); expectingServer.current = false; if (status === 'stale') setStatus('idle'); }
    else setStatus('stale');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notes.text]);
  async function save() {
    if (value === notes.text) { setStatus('idle'); return; }
    setStatus('saving');
    expectingServer.current = true;
    const ok = await onSave(value);
    if (!ok || lastServer.current === value) expectingServer.current = false;
    setStatus(ok ? 'saved' : 'failed');
  }
  const count = notes.text.length;
  return (
    <Collapsible open={open} onOpenChange={setOpen} data-slot="working-notes" className="rounded-xl">
      <CollapsibleTrigger asChild>
        <button type="button" className={cn('flex w-full items-center gap-2 rounded-md px-1 text-left text-[13px] font-medium text-beam-text-soft hover:bg-beam-cream-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-beam-signature', phone ? 'min-h-11' : 'min-h-10')}>
          <ChevronRightIcon aria-hidden="true" className={cn('size-4 transition-transform', open && 'rotate-90')} />
          {count > 0 ? `Working notes (${count} characters, not in the report)` : 'Working notes (not in the report)'}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-1 flex flex-col gap-2 rounded-xl border border-dashed border-beam-warning bg-beam-warning-bg p-3">
          <p className="eyebrow text-beam-warning">Not part of the report body</p>
          <textarea
            id={`text-${notes.boxId}`}
            aria-label={`Working notes for ${sectionKey.replace(/_/g, ' ')}`}
            className="min-h-24 w-full resize-y rounded-md border border-beam-field bg-beam-paper p-2 text-base leading-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-beam-signature read-only:bg-beam-cream-deep"
            value={value}
            readOnly={!editable || dictating !== 'idle'}
            onChange={(e) => setValue(e.target.value)}
            onSelect={(e) => { const t = e.currentTarget; caret.current = { start: t.selectionStart, end: t.selectionEnd }; }}
            onBlur={() => { if (status !== 'stale') void save(); }}
          />
          {interim}
          <div className="flex flex-wrap items-center gap-3">
            {editable ? (
              dictating === 'idle' ? (
                <OfflineWrap offline={offline}><Button variant="outline" size="sm" className={phone ? 'h-11' : 'h-8'} disabled={offline} onClick={() => onDictate(caret.current)}>Dictate a note</Button></OfflineWrap>
              ) : dictating === 'finishing' ? <Button variant="outline" size="sm" className={phone ? 'h-11' : 'h-8'} disabled>Finishing</Button>
                : <Button variant="outline" size="sm" className={cn('border-beam-error text-beam-error', phone ? 'h-11' : 'h-8')} onClick={onStop}>Stop dictation</Button>
            ) : null}
            {children}
            <span role="status" className="text-xs text-beam-text-muted">{status === 'saving' ? 'Saving' : status === 'saved' ? 'Saved' : status === 'failed' ? 'Not saved. Your text is kept.' : status === 'stale' ? 'The notes changed elsewhere. Your text is kept.' : ''}</span>
            {status === 'failed' || status === 'stale' ? (
              <>
                <Button variant="outline" size="sm" className={phone ? 'h-11' : 'h-8'} disabled={offline} onClick={() => void save()}>Save again</Button>
                {status === 'stale' ? <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => { setValue(notes.text); setStatus('idle'); }}>Take the saved text</Button> : null}
              </>
            ) : null}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
